import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, isValidPrice } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import handleCallbackAction from '@functions/handleCallbackAction'

const MODAL_KEY = 'EditTaskPricingModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditTaskPricingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    agency, provider, taskType,
  } = modalPayload

  const {
    callbacks: { updateAgency: updateFn },
    updating,
  } = useAgency(agency)

  const {
    entityState,
    entityState: {
      options: {
        task_prices,
      },
    },
    handlers,
  } = useAgencyForm(agency, { validateOn: 'change' })

  const price = digObject(task_prices, `${provider?.id}.${taskType.short_name}`, '')

  const updateTaskPricePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Task Price Updated',
  }

  const saveEnabled = price === '' || isValidPrice(price)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Task" titleSecondLine="Pricing" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label={`${taskType.title} Price`}>
            <input
              name={`options.task_prices.${provider.id}.${taskType.short_name}`}
              type="number"
              value={price}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => handleCallbackAction(updateTaskPricePayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditTaskPricingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
